import React, { useEffect, useRef } from "react";
import { get_color } from "../../report-visuals";
import _ from "lodash";
import { importAmCharts5Core, importAmCharts5Percent, importAmCharts5Theme } from "../../libs/amcharts5/imports";
import { useIsMobile } from "../../custom-hooks";
import { theme } from "../../pages";

export const DonutChart3d = (props: any) => {
  const { data, _label, value, colors = [], title = "" } = props;
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (typeof window === "undefined" || !data || !containerRef.current) return;

    let chart_data = data;
    const defaultColorsList = chart_data.map((x: any, ind: number) => {
      return get_color(ind);
    });

    chart_data = _.orderBy(chart_data, value, "desc");

    const initChart = async () => {
      try {
        // Lazy load AmCharts 5 modules
        await importAmCharts5Core();
        await importAmCharts5Percent();
        await importAmCharts5Theme("Animated");

        const am5 = (window as any).am5;
        const am5percent = (window as any).am5percent;
        const AnimatedTheme = (window as any).am5themes_Animated;

        if (!am5 || !am5percent) {
          console.warn("amCharts5 not loaded");
          return;
        }

        // Dispose previous chart if exists
        if (chartRef.current) {
          chartRef.current.dispose();
        }

        // Create root element
        const root = am5.Root.new(containerRef.current);
        if (AnimatedTheme) {
          root.setThemes([AnimatedTheme.new(root)]);
        }

        // Create chart
        const chart = root.container.children.push(
          am5percent.PieChart.new(root, {})
        );

        // Create series
        const pieSeries = chart.series.push(
          am5percent.PieSeries.new(root, {
            valueField: value,
            categoryField: _label,
            fillField: "color",
            innerRadius: am5.percent(50),
          })
        );

        pieSeries.slices.template.adapters.add("fill", (fill, target) => {
          const dataContext = target.dataItem?.dataContext as { color?: string };
          if (dataContext?.color) {
            return am5.color(dataContext.color);
          }

          const index = chart_data.findIndex(
            (item) => item[_label] === dataContext?.[_label]
          );
          if (index >= 0 && defaultColorsList[index]) {
            return am5.color(defaultColorsList[index]);
          }

          return fill;
        });

        pieSeries.slices.template.setAll({
          stroke: am5.color("#fff"),
          strokeWidth: 1,
          strokeOpacity: 0.05,
        });

        // Disable labels
        pieSeries.labels.template.setAll({
          visible: false,
        });
        pieSeries.ticks.template.setAll({
          visible: false,
        });

        // Set data
        pieSeries.data.setAll(chart_data);

        // Add legend
        const legend = chart.children.push(
          am5.Legend.new(root, {
            centerX: am5.p50,
            x: am5.p50,
          })
        );
        legend.labels.template.setAll({
          fontSize: isMobile ? 12 : 14,
        });
        legend.markers.template.setAll({
          width: isMobile ? 12 : 14,
          height: isMobile ? 12 : 14,
        });
        legend.markers.template.adapters.add("fill", (fill, target) => {
          const slice = target.dataItem?.dataContext;
          if (slice?.get) {
            return slice.get("fill");
          }
          return fill;
        });
        legend.data.setAll(pieSeries.dataItems);

        // Add center label - in amcharts5, add to chart children for center labels
        if (title) {
          chart.children.push(
            am5.Label.new(root, {
              text: title,
              x: am5.p50,
              y: am5.p50,
              centerX: am5.p50,
              centerY: am5.p50,
              fontSize: 18,
              fontWeight: "bold",
              fill: am5.color(theme || "#000000"),
            })
          );
        }

        // Enable tooltips
        pieSeries.slices.template.set("tooltip", am5.Tooltip.new(root, {
          labelText: `{category}: {value} ({valuePercentTotal.formatNumber("#.0")}%)`,
        }));

        // Animate
        pieSeries.appear(1000, 100);
        chart.appear(1000, 100);

        chartRef.current = root;
      } catch (error) {
        console.error("Error initializing AmCharts 5 chart:", error);
      }
    };

    initChart();

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [data, isMobile, _label, value, title, theme]);

  return (
    <div
      style={{ width: "100%", height: "600px" }}
      className={"flex flex-col mx-auto"}
    >
      <div ref={containerRef} id={title || `chart-${Math.random().toString(36).substr(2, 9)}`} className="w-full h-full" />
    </div>
  );
};
